<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  SOURCES,
  STATUS_BADGE_CLASSES,
  fetchJson,
  normalizeTicimax,
  normalizeShopify,
  normalizeTrendyol,
  formatDate,
  formatCurrency,
  buildOrderDetail,
} from '../helpers/orderHelpers';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const isLoading = ref(true);
const loadError = ref('');
const order = ref(null);

const orderDetail = computed(() => buildOrderDetail(order.value));

const loadOrder = async () => {
  const { source, orderId } = route.params;
  isLoading.value = true;
  loadError.value = '';
  order.value = null;
  try {
    if (source === 'ticimax') {
      const data = await fetchJson(
        `/ticimax-api/siparis?id=${encodeURIComponent(orderId)}`
      );
      if (!data.order) throw new Error('not found');
      order.value = normalizeTicimax(data.order);
    } else if (source === 'shopify') {
      const data = await fetchJson(
        `/shopify-api/orders/${encodeURIComponent(orderId)}.json`
      );
      if (!data.order) throw new Error('not found');
      order.value = normalizeShopify(data.order);
    } else if (source === 'trendyol_koza' || source === 'trendyol_modax') {
      const rawStr = window.history.state?.rawOrder;
      if (!rawStr)
        throw new Error(
          'Sipariş verisi bulunamadı. Lütfen listeye dönüp tekrar tıklayın.'
        );
      const correctedCommission = window.history.state?.correctedCommission;
      order.value = normalizeTrendyol(
        JSON.parse(rawStr),
        source,
        correctedCommission
      );
    } else {
      throw new Error('unknown source');
    }
    // DB'den agent/konuşma bağlamını çek ve agentContext'i güncelle
    try {
      const ctx = await fetchJson(
        `/ticimax-api/order-context?platform=${encodeURIComponent(source)}&order_id=${encodeURIComponent(orderId)}`
      );
      if (ctx.found) {
        order.value = {
          ...order.value,
          agentContext: {
            agentName: ctx.agentName || '',
            channel: ctx.contactIdentifier ? 'Instagram' : '',
            handle: ctx.contactIdentifier || '',
            conversationUrl: ctx.conversationUrl || '',
          },
        };
      }
    } catch {
      // bağlam yoksa sessizce geç
    }
  } catch (error) {
    loadError.value = t('ORDERS.DETAIL.NOT_FOUND');
  } finally {
    isLoading.value = false;
  }
};

const goBack = () => {
  router.push({ name: 'orders_index' });
};

onMounted(loadOrder);
watch(() => [route.params.source, route.params.orderId], loadOrder);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center gap-3 px-4 py-3 border-b sm:px-6 sm:py-4 border-n-weak flex-shrink-0"
    >
      <button
        type="button"
        class="flex items-center justify-center rounded-lg size-9 text-n-slate-11 hover:bg-n-alpha-1"
        @click="goBack"
      >
        <span class="size-5 i-lucide-arrow-left" />
      </button>
      <div class="min-w-0">
        <div class="flex flex-wrap items-center gap-2">
          <h1 class="text-lg font-medium truncate sm:text-xl text-n-slate-12">
            {{
              orderDetail ? orderDetail.orderInfo.no : t('ORDERS.DETAIL.TITLE')
            }}
          </h1>
          <span
            v-if="order"
            class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
            :class="SOURCES[order.source].badge"
          >
            <span
              class="rounded-full size-2"
              :class="SOURCES[order.source].dot"
            />
            {{ SOURCES[order.source].label }}
          </span>
        </div>
        <p v-if="orderDetail" class="mt-0.5 text-sm truncate text-n-slate-11">
          {{ formatDate(orderDetail.orderInfo.date) }} ·
          {{ orderDetail.orderInfo.channel }}
        </p>
      </div>
    </header>

    <div class="flex-1 overflow-y-auto">
      <div
        v-if="isLoading"
        class="flex flex-col items-center justify-center h-full gap-3 text-n-slate-11"
      >
        <Spinner />
        <p class="text-sm">{{ t('ORDERS.DETAIL.LOADING') }}</p>
      </div>

      <div
        v-else-if="loadError || !orderDetail"
        class="flex flex-col items-center justify-center h-full gap-3 text-n-slate-11"
      >
        <span class="size-9 i-lucide-alert-triangle text-n-ruby-9" />
        <p class="text-sm">{{ loadError || t('ORDERS.DETAIL.NOT_FOUND') }}</p>
        <button
          type="button"
          class="px-3 py-1.5 text-sm font-medium rounded-lg outline outline-1 outline-n-weak hover:bg-n-alpha-1"
          @click="goBack"
        >
          {{ t('ORDERS.DETAIL.BACK_TO_LIST') }}
        </button>
      </div>

      <div
        v-else
        class="grid max-w-5xl grid-cols-1 gap-6 px-4 py-5 mx-auto sm:px-6 lg:grid-cols-3"
      >
        <!-- Sol sütun: müşteri, sipariş, kargo ve temsilci bilgileri -->
        <div class="flex flex-col order-2 gap-6 lg:order-1 lg:col-span-1">
          <section class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
            <h3
              class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              {{ t('ORDERS.DETAIL.CUSTOMER') }}
            </h3>
            <ul class="space-y-2 text-sm text-n-slate-12">
              <li class="flex items-center gap-2">
                <span
                  class="flex-shrink-0 size-4 i-lucide-user text-n-slate-10"
                />
                {{ orderDetail.customer.name || '—' }}
              </li>
              <li class="flex items-center gap-2">
                <span
                  class="flex-shrink-0 size-4 i-lucide-phone text-n-slate-10"
                />
                {{ orderDetail.customer.phone || '—' }}
              </li>
              <li class="flex items-center gap-2">
                <span
                  class="flex-shrink-0 size-4 i-lucide-mail text-n-slate-10"
                />
                {{ orderDetail.customer.email || '—' }}
              </li>
            </ul>
          </section>

          <!-- Müşteri temsilcisi / sosyal medya bağlamı: özellik henüz aktif değil,
               bu yüzden alanlar boş görünür — aktive edildiğinde otomatik dolacaktır. -->
          <section class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
            <h3
              class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              {{ t('ORDERS.DETAIL.AGENT_CONTEXT') }}
            </h3>
            <ul class="space-y-2 text-sm text-n-slate-12">
              <li class="flex items-center gap-2">
                <span
                  class="flex-shrink-0 size-4 i-lucide-user-round text-n-slate-10"
                />
                <span class="text-n-slate-10"
                  >{{ t('ORDERS.DETAIL.AGENT_NAME') }}:</span
                >
                <span>{{ orderDetail.agentContext.agentName || '—' }}</span>
              </li>
              <li class="flex items-center gap-2">
                <span
                  class="flex-shrink-0 size-4 i-lucide-instagram text-n-slate-10"
                />
                <span class="text-n-slate-10"
                  >{{ t('ORDERS.DETAIL.SOCIAL_HANDLE') }}:</span
                >
                <span>{{ orderDetail.agentContext.handle || '—' }}</span>
                <span
                  v-if="orderDetail.agentContext.channel"
                  class="text-n-slate-11"
                  >({{ orderDetail.agentContext.channel }})</span
                >
              </li>
              <li
                v-if="orderDetail.agentContext.conversationUrl"
                class="flex items-center gap-2"
              >
                <a
                  :href="orderDetail.agentContext.conversationUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-2 text-n-brand hover:underline"
                >
                  <span class="flex-shrink-0 size-4 i-lucide-external-link" />
                  {{ t('ORDERS.DETAIL.OPEN_CONVERSATION') }}
                </a>
              </li>
            </ul>
          </section>

          <section class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
            <h3
              class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              {{ t('ORDERS.DETAIL.ORDER_INFO') }}
            </h3>
            <dl class="grid grid-cols-2 text-sm gap-x-4 gap-y-2">
              <dt class="text-n-slate-10">{{ t('ORDERS.DETAIL.STATUS') }}</dt>
              <dd>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="
                    STATUS_BADGE_CLASSES[orderDetail.orderInfo.statusBucket]
                  "
                >
                  {{ orderDetail.orderInfo.status || '—' }}
                </span>
              </dd>
              <dt
                v-if="orderDetail.orderInfo.packagingStatus"
                class="text-n-slate-10"
              >
                {{ t('ORDERS.DETAIL.PACKAGING_STATUS') }}
              </dt>
              <dd
                v-if="orderDetail.orderInfo.packagingStatus"
                class="text-n-slate-12"
              >
                {{ orderDetail.orderInfo.packagingStatus }}
              </dd>
              <dt class="text-n-slate-10">
                {{ t('ORDERS.DETAIL.PAYMENT_TYPE') }}
              </dt>
              <dd class="text-n-slate-12">
                {{ orderDetail.orderInfo.paymentType || '—' }}
              </dd>
              <dt class="text-n-slate-10">{{ t('ORDERS.DETAIL.CHANNEL') }}</dt>
              <dd class="text-n-slate-12">
                {{ orderDetail.orderInfo.channel || '—' }}
              </dd>
            </dl>
          </section>

          <section class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
            <h3
              class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              {{ t('ORDERS.DETAIL.CARGO') }}
            </h3>
            <dl class="grid grid-cols-2 text-sm gap-x-4 gap-y-2">
              <dt class="text-n-slate-10">
                {{ t('ORDERS.DETAIL.CARGO_CARRIER') }}
              </dt>
              <dd class="text-n-slate-12">
                {{ orderDetail.cargo.carrier || '—' }}
              </dd>
              <dt class="text-n-slate-10">
                {{ t('ORDERS.DETAIL.CARGO_TRACKING_NO') }}
              </dt>
              <dd class="text-n-slate-12">
                <a
                  v-if="orderDetail.cargo.trackingLink"
                  :href="orderDetail.cargo.trackingLink"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-n-brand hover:underline"
                >
                  {{
                    orderDetail.cargo.trackingNo ||
                    orderDetail.cargo.trackingLink
                  }}
                </a>
                <span v-else>{{ orderDetail.cargo.trackingNo || '—' }}</span>
              </dd>
            </dl>
          </section>

          <section class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
            <h3
              class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              {{ t('ORDERS.DETAIL.DELIVERY_ADDRESS') }}
            </h3>
            <div class="text-sm text-n-slate-12">
              <p class="font-medium">{{ orderDetail.address.name || '—' }}</p>
              <p v-if="orderDetail.address.phone" class="text-n-slate-11">
                {{ orderDetail.address.phone }}
              </p>
              <p>{{ orderDetail.address.line || '—' }}</p>
              <p class="text-n-slate-11">{{ orderDetail.address.city }}</p>
            </div>
          </section>
        </div>

        <!-- Sağ sütun: ürünler ve fiyat özeti -->
        <div class="flex flex-col order-1 gap-6 lg:order-2 lg:col-span-2">
          <section class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
            <h3
              class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              {{ t('ORDERS.DETAIL.PRODUCTS') }}
            </h3>
            <ul class="divide-y divide-n-weak">
              <li
                v-for="(product, index) in orderDetail.products"
                :key="index"
                class="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
              >
                <img
                  v-if="product.image"
                  :src="product.image"
                  :alt="product.name"
                  class="object-cover border rounded-lg size-14 flex-shrink-0 border-n-weak bg-n-alpha-1"
                />
                <div
                  v-else
                  class="flex items-center justify-center border rounded-lg size-14 flex-shrink-0 border-n-weak bg-n-alpha-1 text-n-slate-9"
                >
                  <span class="size-5 i-lucide-image" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium truncate text-n-slate-12">
                    {{ product.name }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{ product.variant || '—' }} · {{ t('ORDERS.DETAIL.QTY') }}:
                    {{ product.qty }}
                  </p>
                </div>
                <div class="text-sm text-end flex-shrink-0">
                  <p class="text-n-slate-12">
                    {{
                      formatCurrency(
                        product.unitPrice,
                        orderDetail.priceSummary.currency
                      )
                    }}
                  </p>
                  <p class="font-medium text-n-slate-12">
                    {{
                      formatCurrency(
                        product.total,
                        orderDetail.priceSummary.currency
                      )
                    }}
                  </p>
                </div>
              </li>
            </ul>
          </section>

          <section class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
            <h3
              class="mb-3 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              {{ t('ORDERS.DETAIL.PRICE_SUMMARY') }}
            </h3>
            <dl class="text-sm divide-y divide-n-weak">
              <div class="flex items-center justify-between py-1.5">
                <dt class="text-n-slate-11">
                  {{ t('ORDERS.DETAIL.SUBTOTAL') }}
                </dt>
                <dd class="text-n-slate-12">
                  {{
                    formatCurrency(
                      orderDetail.priceSummary.subtotal,
                      orderDetail.priceSummary.currency
                    )
                  }}
                </dd>
              </div>
              <!-- İndirim (Trendyol) -->
              <div
                v-if="orderDetail.priceSummary.discount > 0"
                class="flex items-center justify-between py-1.5 text-red-600 dark:text-red-400"
              >
                <dt>İndirim</dt>
                <dd>
                  -
                  {{
                    formatCurrency(
                      orderDetail.priceSummary.discount,
                      orderDetail.priceSummary.currency
                    )
                  }}
                </dd>
              </div>
              <!-- Komisyon (Trendyol) -->
              <div
                v-if="orderDetail.priceSummary.commission > 0"
                class="flex items-center justify-between py-1.5 text-orange-600 dark:text-orange-400"
              >
                <dt>Komisyon</dt>
                <dd>
                  -
                  {{
                    formatCurrency(
                      orderDetail.priceSummary.commission,
                      orderDetail.priceSummary.currency
                    )
                  }}
                </dd>
              </div>
              <div
                v-if="
                  orderDetail.priceSummary.vat != null &&
                  orderDetail.priceSummary.vat > 0
                "
                class="flex items-center justify-between py-1.5"
              >
                <dt class="text-n-slate-11">{{ t('ORDERS.DETAIL.VAT') }}</dt>
                <dd class="text-n-slate-12">
                  {{
                    formatCurrency(
                      orderDetail.priceSummary.vat,
                      orderDetail.priceSummary.currency
                    )
                  }}
                </dd>
              </div>
              <div
                v-if="orderDetail.priceSummary.shipping > 0"
                class="flex items-center justify-between py-1.5"
              >
                <dt class="text-n-slate-11">
                  {{ t('ORDERS.DETAIL.SHIPPING') }}
                </dt>
                <dd class="text-n-slate-12">
                  {{
                    formatCurrency(
                      orderDetail.priceSummary.shipping,
                      orderDetail.priceSummary.currency
                    )
                  }}
                </dd>
              </div>
              <div
                v-if="orderDetail.priceSummary.cod > 0"
                class="flex items-center justify-between py-1.5"
              >
                <dt class="text-n-slate-11">{{ t('ORDERS.DETAIL.COD') }}</dt>
                <dd class="text-n-slate-12">
                  {{
                    formatCurrency(
                      orderDetail.priceSummary.cod,
                      orderDetail.priceSummary.currency
                    )
                  }}
                </dd>
              </div>
              <div class="flex items-center justify-between py-2 font-medium">
                <dt class="text-n-slate-12">
                  {{ t('ORDERS.DETAIL.GRAND_TOTAL') }}
                </dt>
                <dd class="text-n-slate-12">
                  {{
                    formatCurrency(
                      orderDetail.priceSummary.total,
                      orderDetail.priceSummary.currency
                    )
                  }}
                </dd>
              </div>
            </dl>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>
